import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DoorOpen, CalendarDays, Users, Armchair, Plus, Settings, ExternalLink, Copy, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

const RoomCard = ({ room, owned }) => {
  const { t } = useI18n();
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/r/${room.full_slug}`);
    toast.success(t("room.copied"));
  };
  return (
    <div data-testid={`room-card-${room.full_slug.replace(/\//g, "-")}`} className="card-hover bg-[#131B2A] border border-slate-800 rounded-2xl p-6 flex flex-col">
      <div className="flex items-start justify-between">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${room.color}22`, border: `1px solid ${room.color}55` }}>
          <DoorOpen className="w-5 h-5" style={{ color: room.color }} />
        </span>
        <div className="flex items-center gap-1.5">
          <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full ${room.access_type === "private" ? "bg-amber-500/10 text-amber-400 border border-amber-500/30" : "bg-sky-500/10 text-sky-400 border border-sky-500/30"}`}>
            {room.access_type === "private" ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            {room.access_type === "private" ? t("room.private") : t("room.public")}
          </span>
          <span className={`flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-full ${room.is_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border border-slate-700"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${room.is_active ? "bg-emerald-400 live-dot" : "bg-slate-500"}`} />
            {room.is_active ? t("room.active") : t("room.inactive")}
          </span>
        </div>
      </div>
      <h3 className="mt-4 text-lg font-semibold text-white">{room.name}</h3>
      <span className="mt-1 font-mono text-xs text-indigo-300 truncate">digisidlo.cz/{room.full_slug}</span>
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Armchair className="w-3.5 h-3.5" /> {room.capacity} {t("room.seats")}</span>
        <span>{room.parent_id ? t("room.sub_office") : t("room.main_address")}</span>
      </div>
      <div className="mt-5 pt-4 border-t border-slate-800 flex items-center gap-2">
        <Link to={`/r/${room.full_slug}`} data-testid={`enter-room-${room.full_slug.replace(/\//g, "-")}`}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> {t("room.enter")}
        </Link>
        <button onClick={copyLink} data-testid={`copy-link-${room.full_slug.replace(/\//g, "-")}`}
          className="p-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
          <Copy className="w-4 h-4" />
        </button>
        {owned && (
          <Link to={`/rooms/${room.id}/manage`} data-testid={`manage-room-${room.full_slug.replace(/\//g, "-")}`}
            className="p-2 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-indigo-500 transition-colors">
            <Settings className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [rooms, setRooms] = useState({ owned: [], shared: [] });
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/events/upcoming").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const kpis = stats ? [
    { label: t("dash.kpi_rooms"), value: stats.active_rooms, icon: DoorOpen },
    { label: t("dash.kpi_events"), value: stats.upcoming_events, icon: CalendarDays },
    { label: t("dash.kpi_members"), value: stats.members, icon: Users },
    { label: t("dash.kpi_capacity"), value: stats.total_capacity, icon: Armchair },
  ] : [];

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20" data-testid="dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-400">{t("dash.label")}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{t("dash.welcome")}, {user?.name}</h1>
          </div>
          <Link to="/rooms/new" data-testid="create-room-button"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-colors">
            <Plus className="w-4 h-4" /> {t("dash.create_room")}
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} data-testid={`kpi-${k.label}`} className="bg-[#131B2A] border border-slate-800 rounded-2xl p-6">
              <k.icon className="w-5 h-5 text-indigo-400" />
              <div className="mt-3 font-mono text-3xl font-semibold text-white">{k.value}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">{k.label}</div>
            </div>
          ))}
        </div>

        <section className="mt-12">
          <h2 className="text-xl font-bold text-white">{t("dash.my_rooms")}</h2>
          {rooms.owned.length === 0 ? (
            <div data-testid="empty-rooms" className="mt-6 bg-[#131B2A] border border-dashed border-slate-700 rounded-2xl p-12 text-center">
              <DoorOpen className="w-8 h-8 text-slate-600 mx-auto" />
              <p className="mt-4 text-slate-400">{t("dash.no_rooms")}</p>
              <Link to="/rooms/new" className="mt-4 inline-block px-6 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors">
                {t("dash.create_first")}
              </Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.owned.map((r) => <RoomCard key={r.id} room={r} owned />)}
            </div>
          )}
        </section>

        {rooms.shared.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-white">{t("dash.shared")}</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.shared.map((r) => <RoomCard key={r.id} room={r} owned={false} />)}
            </div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="text-xl font-bold text-white">{t("dash.upcoming")}</h2>
          {events.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">{t("dash.no_events")}</p>
          ) : (
            <div className="mt-6 space-y-3">
              {events.map((e) => (
                <div key={e.id} data-testid={`upcoming-event-${e.id}`} className="flex items-center gap-4 bg-[#131B2A] border border-slate-800 rounded-xl px-5 py-4">
                  <CalendarDays className="w-5 h-5 text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{e.title}</div>
                    <div className="text-xs text-slate-500">{e.room_name} · {new Date(e.start_time).toLocaleString("cs-CZ")}{e.recurrence !== "none" && ` · ${t("dash.recurs")}`}</div>
                  </div>
                  <Link to={`/r/${e.room_slug}`} className="text-sm text-indigo-400 hover:text-indigo-300 shrink-0">{t("dash.to_room")}</Link>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
